import type { Product } from 'app/features/recommend/interfaces/models';
import { useKeenSlider } from 'keen-slider/react';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';
import 'keen-slider/keen-slider.min.css';
import styles from './RecommendationShoes.module.scss';
import { CommonImage } from '~/components/common/image/CommonImage';
import type { ImpListName } from '~/features/recommend/hooks/useCommonProductsImpression';
import { useCommonProductsImpression } from '~/features/recommend/hooks/useCommonProductsImpression';
import { isATRShoes } from '~/features/recommend/utils/isATRShoes';

interface Props {
  products: Product[];
  listName?: ImpListName;
  selectedProductIndex?: number;
  setSelectedProductIndex: Dispatch<SetStateAction<number>>;
  onItemClick?: () => void;
  imageType?: 'alt' | 'side';
}

export const RecommendationShoes = ({
  products,
  listName,
  setSelectedProductIndex,
  selectedProductIndex = 0,
  onItemClick,
  imageType = 'alt',
}: Props) => {
  const observer = useRef<HTMLDivElement>(null);
  const initialIndexRef = useRef(selectedProductIndex);

  useEffect(() => {
    setSelectedProductIndex(selectedProductIndex);
  }, [products]);

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: true,
    slides: {
      origin: 'center',
      perView: 1.7,
      spacing: 16,
    },
    initial: initialIndexRef.current,
    slideChanged: (slider) => {
      const currentIndex = slider.track.details.rel;
      setSelectedProductIndex(currentIndex);
    },
  });

  useCommonProductsImpression(listName, observer, products);

  if (!products.length) return <></>;

  return (
    <div className={styles.container} ref={observer}>
      <div ref={sliderRef} className="keen-slider" key={JSON.stringify(products)}>
        {products.map((product, index) => {
          const imageURL =
            imageType === 'side'
              ? product.image
              : product.alt_image
              ? product.alt_image
              : product.image;

          const currentActiveIndex = instanceRef.current?.track.details.rel ?? selectedProductIndex;

          const getSlidePosition = (
            currentIndex: number,
            targetIndex: number,
            totalSlides: number,
          ) => {
            if (currentIndex === targetIndex) return 'active';
            const prevIndex = (currentIndex - 1 + totalSlides) % totalSlides;
            if (targetIndex === prevIndex) return 'prev';

            return 'next';
          };

          const slidePosition = getSlidePosition(currentActiveIndex, index, products.length);

          return (
            <div
              key={product.product_id}
              className="keen-slider__slide"
              onClick={onItemClick && onItemClick}
            >
              <div
                className={`${styles['slide-wrapper']} ${
                  slidePosition === 'active'
                    ? styles['slide-wrapper--active']
                    : slidePosition === 'prev'
                    ? styles['slide-wrapper--prev']
                    : styles['slide-wrapper--next']
                }`}
              >
                <CommonImage
                  src={imageURL}
                  alt={product.name}
                  width={370}
                  height={278}
                  objectFit={isATRShoes(imageURL) ? 'cover' : 'contain'}
                  objectPosition="center"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
